// Guard sign-in for Guard Web, on top of supabase-js Auth.
//
// supabase-js owns the GoTrue password grant, token refresh, AND session persistence
// (persistSession + autoRefreshToken in ../lib/supabase-client.ts) — so "stay-logged-in" is
// library-native. This module keeps only the thin policy on top:
//   - SAFE_SIGN_IN_FAILURE discipline: ALL failure paths (bad creds, transport, malformed) return
//     ONE fixed generic message. The underlying GoTrue/AuthError is NEVER surfaced — it can
//     distinguish "no such user" from "bad password" and may echo the email.
//   - Pre-network input hygiene (validateCredentials) for inline form hints.
// Mirrors the console's auth invariants (apps/console/src/auth/auth.ts).

import type { SupabaseClient } from "@supabase/supabase-js";

/** The single SAFE, generic sign-in failure message — identical across every failure path so the
 *  surface cannot be used to tell "bad password" from "no such user" or to leak anything. */
export const SAFE_SIGN_IN_FAILURE = "Sign-in failed — check your email and password.";

// Lenient email shape: a single @ with non-empty, space-free local/domain parts and a dotted
// domain. Input hygiene only — Supabase GoTrue is the real arbiter.
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export interface Credentials {
  email: string;
  password: string;
}

export type ValidateCredentialsResult = { ok: true } | { ok: false; reason: string };

/**
 * Validate inputs BEFORE any network call: a non-empty, plausibly-shaped email and a non-empty
 * password. The failure `reason` names the offending field but NEVER includes the value — safe to
 * show inline in the form.
 */
export function validateCredentials(input: Credentials): ValidateCredentialsResult {
  if (typeof input.email !== "string" || input.email.trim().length === 0) {
    return { ok: false, reason: "Email is required." };
  }
  if (!EMAIL_PATTERN.test(input.email.trim())) {
    return { ok: false, reason: "Email is not a valid address." };
  }
  // Presence only — never logged or echoed; the trim is an emptiness test, not a transform.
  if (typeof input.password !== "string" || input.password.trim().length === 0) {
    return { ok: false, reason: "Password is required." };
  }
  return { ok: true };
}

export type SignInResult = { ok: true } | { ok: false; reason: string };

/**
 * Sign a guard in via supabase-js (`signInWithPassword`). On success supabase-js persists +
 * auto-refreshes the session; the AuthProvider's onAuthStateChange flips the UI to the shell.
 * On ANY failure returns { ok:false, reason: SAFE_SIGN_IN_FAILURE } — the AuthError is never read.
 */
export async function signInWithSupabase(
  client: SupabaseClient,
  credentials: Credentials,
): Promise<SignInResult> {
  const validation = validateCredentials(credentials);
  if (!validation.ok) {
    // The form uses validateCredentials directly for field hints; the sign-in boundary itself
    // still collapses to the generic message.
    return { ok: false, reason: SAFE_SIGN_IN_FAILURE };
  }
  try {
    const { data, error } = await client.auth.signInWithPassword({
      email: credentials.email.trim(),
      password: credentials.password,
    });
    if (error !== null || data.session === null) {
      return { ok: false, reason: SAFE_SIGN_IN_FAILURE };
    }
    return { ok: true };
  } catch {
    return { ok: false, reason: SAFE_SIGN_IN_FAILURE };
  }
}

/** Sign out (best-effort). onAuthStateChange drives the UI back to unauthenticated regardless. */
export async function signOut(client: SupabaseClient): Promise<void> {
  try {
    await client.auth.signOut();
  } catch {
    // Never surface a sign-out error; the local session clear still unwinds the UI.
  }
}
